import { ContextMenuType } from '@/app/atoms/contextMenuAtom';
import { deactivateFormatPainter, getFormatPainterState } from '@/app/atoms/formatPainterAtom';
import { PanMode } from '@/app/atoms/gridPanModeAtom';
import { getMobileMode, setMobileMode } from '@/app/atoms/mobileStateAtom';
import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import type { TablePointerDownResult } from '@/app/gridGL/cells/tables/Tables';
import { inlineEditorHandler } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorHandler';
import { inlineEditorMonaco } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorMonaco';
import { doubleClickCell } from '@/app/gridGL/interaction/pointer/doubleClickCell';
import { DOUBLE_CLICK_TIME } from '@/app/gridGL/interaction/pointer/pointerUtils';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { isMobileMode } from '@/app/helpers/isMobile';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import { isLinux } from '@/shared/utils/isLinux';
import { isMac } from '@/shared/utils/isMac';
import { Point, Rectangle, type FederatedPointerEvent } from 'pixi.js';

const MINIMUM_MOVE_POSITION = 5;

// Mobile long-press duration to open the table context menu (matches the
// threshold used by `useLongPress` for HTML elements).
const MOBILE_LONG_PRESS_DURATION = 500;

export class PointerDown {
  active = false;

  private positionRaw?: Point;
  private position?: Point;
  private pointerMoved = false;
  private doubleClickTimeout?: number;

  // the mouse's last column and row
  previousPosition?: Point;

  // the mouse's last column and row when double clicked
  doubleClickPreviousPosition?: Point;

  // used to track the unselect rectangle
  unselectDown?: Rectangle;

  // track right-click for drag selection (context menu shown on pointer up if not dragged)
  private rightClickDown?: { world: Point; column: number; row: number };

  // track mobile view mode tap - only select cell if finger doesn't move
  private mobileViewTap?: { column: number; row: number };

  // track mobile cellSelected mode interaction
  // onSelectedCell: drag from selected cell to expand selection
  // onOtherCell: tap on different cell to move cursor (pan if drag)
  private mobileCellSelectedTap?: {
    column: number;
    row: number;
    onSelectedCell: boolean;
  };

  // track mobile double-tap detection
  private mobileDoubleTapTimeout?: number;
  private mobileDoubleTapPosition?: { column: number; row: number };

  // track when mobile viewport panning is active (to ignore in pointerUp)
  private mobileViewportPanning = false;

  // track screen position for mobile tap detection (separate from world position
  // because viewport panning changes world coords but screen coords stay relative to finger)
  private mobileScreenPosition?: Point;

  // track mobile long-press to open the table context menu
  private mobileLongPressTimeout?: number;
  private mobileLongPressWorld?: Point;

  // Determines whether the input is valid (ie, whether it is open and has a value that does not fail validation)
  private async isInputValid(): Promise<boolean> {
    const location = inlineEditorHandler.location;
    if (!location || !inlineEditorHandler.isOpen()) return true;
    const validationError = await inlineEditorHandler.validateInput();
    if (validationError) {
      events.emit('hoverCell', {
        x: location.x,
        y: location.y,
        validationId: validationError,
        value: inlineEditorMonaco.get(),
      });
      inlineEditorMonaco.focus();
      return false;
    }
    return true;
  }

  async pointerDown(world: Point, event: FederatedPointerEvent) {
    const isMiddleClick = event.button === 1;
    // to prevent default paste behavior on middle click, in Linux
    if (isLinux && isMiddleClick) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (gridSettings.getPanMode() !== PanMode.Disabled || event.button === 1) return;

    // Mobile view mode: track tap position, only select on pointerUp if no movement
    if (isMobileMode() && getMobileMode() === 'view') {
      const sheet = sheets.sheet;
      const { column, row } = sheet.getColumnRowFromScreen(world.x, world.y);
      this.mobileViewTap = { column, row };
      this.mobileScreenPosition = event.global.clone();
      this.pointerMoved = false;
      this.startMobileLongPress(world);
      return;
    }

    const sheet = sheets.sheet;
    const cursor = sheet.cursor;

    // Mobile cellSelected/multiSelect mode: special handling for touch
    if (isMobileMode() && (getMobileMode() === 'cellSelected' || getMobileMode() === 'multiSelect')) {
      const { column, row } = sheet.getColumnRowFromScreen(world.x, world.y);
      const onSelectedCell = cursor.contains(column, row);

      this.mobileCellSelectedTap = { column, row, onSelectedCell };
      this.mobileScreenPosition = event.global.clone();
      this.pointerMoved = false;

      if (onSelectedCell) {
        // Start tracking for potential drag-to-expand selection
        // Pause viewport dragging so we can expand selection without panning
        pixiApp.viewport.pauseDrag();
        this.previousPosition = new Point(column, row);
        this.position = new Point(column, row);
        this.active = true;
      }
      this.startMobileLongPress(world);
      return;
    }

    this.positionRaw = world;
    const { column, row } = sheet.getColumnRowFromScreen(world.x, world.y);

    const isRightClick = event.button === 2 || (isMac && event.button === 0 && event.ctrlKey);

    // Right-click: start selection (drag to extend), context menu shown on pointer up
    if (isRightClick) {
      if (event.shiftKey) {
        // Shift+right-click: extend selection to this cell
        cursor.selectTo(column, row, event.metaKey || event.ctrlKey);
      } else if (!cursor.contains(column, row)) {
        // Check if cell is covered by an HTML/image - if so, select the table
        const htmlImageTable = content.cellsSheet.tables.getHtmlImageCoveringCell(column, row);
        if (htmlImageTable) {
          cursor.selectTable(htmlImageTable.codeCell.name, undefined, false, event.metaKey || event.ctrlKey);
        } else {
          // If Cmd/Ctrl is held, add to selection; otherwise replace selection
          cursor.moveTo(column, row, { append: event.metaKey || event.ctrlKey });
        }
      }
      // Store right-click info for showing context menu on pointer up
      this.rightClickDown = { world, column, row };
      this.previousPosition = new Point(column, row);
      this.positionRaw = world;
      this.pointerMoved = false;
      this.position = new Point(column, row);
      this.active = true;
      return;
    }

    if (this.doubleClickTimeout) {
      window.clearTimeout(this.doubleClickTimeout);
      this.doubleClickTimeout = undefined;

      if (!(await this.isInputValid())) return;

      if (
        this.doubleClickPreviousPosition &&
        column === this.doubleClickPreviousPosition.x &&
        row === this.doubleClickPreviousPosition.y
      ) {
        // ignore right click
        if (isRightClick) {
          return;
        }
        event.preventDefault();
        doubleClickCell({ column, row });
        this.active = false;
        return;
      }
    }
    // do nothing if we have text is invalid in the input
    if (!(await this.isInputValid())) return;

    if ((event.ctrlKey || event.metaKey) && cursor.contains(column, row)) {
      this.unselectDown = new Rectangle(column, row, 0, 0);
      events.emit('setDirty', { cursor: true });
      return;
    }

    // If the user is holding shift, extend selection from current cursor
    // If also holding cmd/ctrl, it's append mode (add new range)
    if (event.shiftKey) {
      // Shift-click: extend selection from current cursor to clicked position
      // If ctrl/cmd is also pressed, it's append mode (MouseCtrlClick)
      // Otherwise it's MouseShiftClick
      const isAppend = event.metaKey || event.ctrlKey;
      cursor.selectTo(column, row, isAppend, true);
    } else {
      // If the input is rejected, we cannot move the cursor
      if (await inlineEditorHandler.handleCellPointerDown()) {
        // Check if cell is covered by an HTML/image - if so, select the table
        const htmlImageTable = content.cellsSheet.tables.getHtmlImageCoveringCell(column, row);
        if (htmlImageTable) {
          cursor.selectTable(htmlImageTable.codeCell.name, undefined, false, event.metaKey || event.ctrlKey);
        } else {
          cursor.moveTo(column, row, { ensureVisible: false, append: event.metaKey || event.ctrlKey });
        }
      } else {
        inlineEditorMonaco.focus();
      }
    }
    this.previousPosition = new Point(column, row);
    this.doubleClickPreviousPosition = new Point(column, row);
    events.emit('clickedToCell', column, row, world);
    this.pointerMoved = false;
    this.position = new Point(column, row);
    this.active = true;
  }

  pointerMove(world: Point, event: FederatedPointerEvent) {
    if (gridSettings.getPanMode() !== PanMode.Disabled) return;

    const { viewport } = pixiApp;
    const sheet = sheets.sheet;

    // Track movement for mobile view mode tap detection (use screen coords since
    // viewport panning changes world coords but screen position reflects actual finger movement)
    if (this.mobileViewTap && this.mobileScreenPosition) {
      if (
        Math.abs(this.mobileScreenPosition.x - event.global.x) +
          Math.abs(this.mobileScreenPosition.y - event.global.y) >
        MINIMUM_MOVE_POSITION
      ) {
        // Finger moved too much - cancel the tap, allow viewport panning
        this.mobileViewTap = undefined;
        this.mobileScreenPosition = undefined;
        this.pointerMoved = true;
        this.clearMobileLongPress();
      }
      return;
    }

    // Track movement for mobile cellSelected mode (use screen coords since
    // viewport panning changes world coords but screen position reflects actual finger movement)
    if (this.mobileCellSelectedTap && this.mobileScreenPosition) {
      // Check if we've moved enough pixels to start dragging (only matters for initial detection)
      const movedEnough =
        this.pointerMoved ||
        Math.abs(this.mobileScreenPosition.x - event.global.x) +
          Math.abs(this.mobileScreenPosition.y - event.global.y) >
          MINIMUM_MOVE_POSITION;

      if (movedEnough) {
        // Cancel pending long-press as soon as the finger moves outside the
        // tap threshold (regardless of which sub-branch handles the drag).
        this.clearMobileLongPress();
        if (this.mobileCellSelectedTap.onSelectedCell) {
          // Dragging from selected cell - expand selection
          // Enable mouse edges for auto-scrolling at viewport edges
          if (!this.pointerMoved) {
            pixiApp.viewport.enableMouseEdges(world);
          }
          this.pointerMoved = true;
          const { column, row } = sheet.getColumnRowFromScreen(world.x, world.y);

          if (this.previousPosition && (column !== this.previousPosition.x || row !== this.previousPosition.y)) {
            setMobileMode('multiSelect');
            sheet.cursor.selectTo(column, row, false, false);
            this.previousPosition = new Point(column, row);
            events.emit('setDirty', { cursor: true });
          }
        } else {
          // Dragging from non-selected cell - cancel tap, allow viewport panning
          this.mobileCellSelectedTap = undefined;
          this.mobileScreenPosition = undefined;
          this.mobileViewportPanning = true;
          this.pointerMoved = true;
          this.active = false;
        }
      }
      return;
    }

    if (this.unselectDown) {
      const { column, row } = sheet.getColumnRowFromScreen(world.x, world.y);
      this.unselectDown.width = column - this.unselectDown.left;
      this.unselectDown.height = row - this.unselectDown.top;

      // this is necessary to ensure the rectangle always has width/height
      if (this.unselectDown.width < 0) this.unselectDown.width -= 1;
      if (this.unselectDown.height < 0) this.unselectDown.height -= 1;

      events.emit('setDirty', { cursor: true });
      return;
    }

    if (!this.active) return;

    if (!this.pointerMoved && this.positionRaw) {
      if (
        Math.abs(this.positionRaw.x - world.x) + Math.abs(this.positionRaw.y - world.y) >
        MINIMUM_MOVE_POSITION / viewport.scale.x
      ) {
        this.pointerMoved = true;
        this.clearDoubleClick();
      } else {
        return;
      }
    }

    // cursor intersects bottom-corner indicator (disabled for now)
    if (!this.active || !this.position || !this.previousPosition || !this.positionRaw) {
      return;
    }

    // calculate mouse move position
    const { column, row } = sheet.getColumnRowFromScreen(world.x, world.y);

    if (column !== this.previousPosition.x || row !== this.previousPosition.y) {
      pixiApp.viewport.enableMouseEdges(world);

      sheet.cursor.selectTo(column, row, event.ctrlKey || event.metaKey, false);
      this.previousPosition = new Point(column, row);

      if (inlineEditorHandler.isOpen() && !inlineEditorHandler.isEditingFormula()) {
        gridSettings.changeInput(false);
      }
    }
  }

  pointerUp(event?: FederatedPointerEvent) {
    const isMiddleClick = event && event.button === 1;
    // to prevent default paste behavior on middle click, in Linux
    if (isLinux && isMiddleClick) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Always cancel a pending long-press timer on release; if it had already
    // fired, the timer is already cleared and the tap state was reset, so the
    // remaining branches will fall through harmlessly.
    this.clearMobileLongPress();

    // Mobile viewport panning completed - just reset flag and exit
    if (this.mobileViewportPanning) {
      this.mobileViewportPanning = false;
      return;
    }

    // Mobile view mode: if tap completed without movement, select the cell
    if (this.mobileViewTap) {
      const { column, row } = this.mobileViewTap;
      this.mobileViewTap = undefined;
      this.mobileScreenPosition = undefined;
      setMobileMode('cellSelected');

      // Check if tap is on a table header, column header, or chart body
      if (event) {
        const world = pixiApp.viewport.toWorld(event.global);
        const tableDown = content.cellsSheet.tables.pointerDown(world, false);
        if (tableDown) {
          if (tableDown.type === 'table-name' || tableDown.type === 'chart') {
            sheets.sheet.cursor.selectTable(tableDown.table.name, undefined, false, false);
            events.emit('setDirty', { cursor: true });
            // Start double-tap timer for the clicked position (doubleClickCell will find the table)
            this.clearMobileDoubleTap();
            this.mobileDoubleTapPosition = { column, row };
            this.mobileDoubleTapTimeout = window.setTimeout(() => {
              this.mobileDoubleTapTimeout = undefined;
              this.mobileDoubleTapPosition = undefined;
            }, DOUBLE_CLICK_TIME);
            return;
          } else if (tableDown.type === 'column-name' && tableDown.column !== undefined) {
            const columnName = tableDown.table.columns[tableDown.column].name;
            sheets.sheet.cursor.selectTable(tableDown.table.name, columnName, false, false);
            events.emit('setDirty', { cursor: true });
            // Start double-tap timer for the clicked position (doubleClickCell will find the table)
            this.clearMobileDoubleTap();
            this.mobileDoubleTapPosition = { column, row };
            this.mobileDoubleTapTimeout = window.setTimeout(() => {
              this.mobileDoubleTapTimeout = undefined;
              this.mobileDoubleTapPosition = undefined;
            }, DOUBLE_CLICK_TIME);
            return;
          }
        }
      }

      sheets.sheet.cursor.moveTo(column, row, { ensureVisible: true });
      events.emit('setDirty', { cursor: true });
      // Start double-tap timer for this cell
      this.clearMobileDoubleTap();
      this.mobileDoubleTapPosition = { column, row };
      this.mobileDoubleTapTimeout = window.setTimeout(() => {
        this.mobileDoubleTapTimeout = undefined;
        this.mobileDoubleTapPosition = undefined;
      }, DOUBLE_CLICK_TIME);
      return;
    }

    // Mobile cellSelected mode: handle tap completion
    if (this.mobileCellSelectedTap) {
      const { column, row, onSelectedCell } = this.mobileCellSelectedTap;
      this.mobileCellSelectedTap = undefined;
      this.mobileScreenPosition = undefined;
      this.active = false;
      this.previousPosition = undefined;

      // Resume viewport dragging and disable mouse edges if we were expanding selection
      if (onSelectedCell) {
        pixiApp.viewport.resumeDrag();
        pixiApp.viewport.disableMouseEdges();
      }

      if (!this.pointerMoved) {
        if (onSelectedCell) {
          // Tap on already selected cell - check for double-tap to open editor
          if (
            this.mobileDoubleTapTimeout &&
            this.mobileDoubleTapPosition &&
            this.mobileDoubleTapPosition.column === column &&
            this.mobileDoubleTapPosition.row === row
          ) {
            // Double-tap detected - open the editor
            window.clearTimeout(this.mobileDoubleTapTimeout);
            this.mobileDoubleTapTimeout = undefined;
            this.mobileDoubleTapPosition = undefined;
            doubleClickCell({ column, row });
            return;
          } else {
            // First tap - start double-tap timer
            this.clearMobileDoubleTap();
            this.mobileDoubleTapPosition = { column, row };
            this.mobileDoubleTapTimeout = window.setTimeout(() => {
              this.mobileDoubleTapTimeout = undefined;
              this.mobileDoubleTapPosition = undefined;
            }, DOUBLE_CLICK_TIME);
          }
        } else {
          // Tap on different cell - check if it's a table/chart first
          if (event) {
            const world = pixiApp.viewport.toWorld(event.global);
            const tableDown = content.cellsSheet.tables.pointerDown(world, false);
            if (tableDown) {
              if (tableDown.type === 'table-name' || tableDown.type === 'chart') {
                sheets.sheet.cursor.selectTable(tableDown.table.name, undefined, false, false);
                events.emit('setDirty', { cursor: true });
                // Start double-tap timer for the clicked position (doubleClickCell will find the table)
                this.clearMobileDoubleTap();
                this.mobileDoubleTapPosition = { column, row };
                this.mobileDoubleTapTimeout = window.setTimeout(() => {
                  this.mobileDoubleTapTimeout = undefined;
                  this.mobileDoubleTapPosition = undefined;
                }, DOUBLE_CLICK_TIME);
                return;
              } else if (tableDown.type === 'column-name' && tableDown.column !== undefined) {
                const columnName = tableDown.table.columns[tableDown.column].name;
                sheets.sheet.cursor.selectTable(tableDown.table.name, columnName, false, false);
                events.emit('setDirty', { cursor: true });
                // Start double-tap timer for the clicked position (doubleClickCell will find the table)
                this.clearMobileDoubleTap();
                this.mobileDoubleTapPosition = { column, row };
                this.mobileDoubleTapTimeout = window.setTimeout(() => {
                  this.mobileDoubleTapTimeout = undefined;
                  this.mobileDoubleTapPosition = undefined;
                }, DOUBLE_CLICK_TIME);
                return;
              }
            }
          }
          // Not a table - move cursor there
          sheets.sheet.cursor.moveTo(column, row, { ensureVisible: true });
          setMobileMode('cellSelected');
          events.emit('setDirty', { cursor: true });
          // Start double-tap timer for this cell
          this.clearMobileDoubleTap();
          this.mobileDoubleTapPosition = { column, row };
          this.mobileDoubleTapTimeout = window.setTimeout(() => {
            this.mobileDoubleTapTimeout = undefined;
            this.mobileDoubleTapPosition = undefined;
          }, DOUBLE_CLICK_TIME);
        }
      }
      // If pointerMoved was true, viewport panning happened - no selection change
      return;
    }

    if (this.unselectDown) {
      sheets.sheet.cursor.excludeCells(
        this.unselectDown.left,
        this.unselectDown.top,
        this.unselectDown.right,
        this.unselectDown.bottom
      );
      this.unselectDown = undefined;
      events.emit('setDirty', { cursor: true });
      return;
    }

    // Show context menu on right-click release
    if (this.rightClickDown) {
      const { world, column, row } = this.rightClickDown;
      const cursor = sheets.sheet.cursor;
      const codeCell = content.cellsSheet.tables.getCodeCellIntersects({ x: column, y: row });
      if (codeCell && codeCell?.language !== 'Import') {
        events.emit('contextMenu', {
          type: ContextMenuType.Grid,
          world,
          column: codeCell.x,
          row: codeCell.y,
          table: codeCell,
        });
      } else {
        events.emit('contextMenu', {
          type: ContextMenuType.Grid,
          world,
          column,
          row,
          // _could_ have an associated table if it's a cell inside a table on the grid
          table: content.cellsSheet.tables.getCodeCellIntersects(cursor.position),
        });
      }
      this.rightClickDown = undefined;
      this.active = false;
      this.previousPosition = undefined;
      pixiApp.viewport.disableMouseEdges();
      return;
    }

    if (this.active) {
      // Handle format painter: apply formatting on pointer up
      const formatPainterState = getFormatPainterState();
      if (formatPainterState.active && formatPainterState.sourceSelection) {
        const targetSelection = sheets.sheet.cursor.save();
        quadraticCore.applyFormatPainter(formatPainterState.sourceSelection, targetSelection, false);
        deactivateFormatPainter();
        events.emit('formatPainterEnd');

        // Ensure the selection stays on the target cells
        sheets.sheet.cursor.load(targetSelection);
        events.emit('cursorPosition');
      }

      if (!this.pointerMoved) {
        this.doubleClickTimeout = window.setTimeout(() => (this.doubleClickTimeout = undefined), DOUBLE_CLICK_TIME);
      }
      this.active = false;
      this.previousPosition = undefined;
      pixiApp.viewport.disableMouseEdges();
    }
  }

  private clearDoubleClick() {
    if (this.doubleClickTimeout) {
      window.clearTimeout(this.doubleClickTimeout);
      this.doubleClickTimeout = undefined;
    }
  }

  private clearMobileDoubleTap() {
    if (this.mobileDoubleTapTimeout) {
      window.clearTimeout(this.mobileDoubleTapTimeout);
      this.mobileDoubleTapTimeout = undefined;
    }
    this.mobileDoubleTapPosition = undefined;
  }

  // Schedules the mobile long-press handler. Fires after
  // MOBILE_LONG_PRESS_DURATION ms unless the touch ends or moves outside the
  // tap threshold first. Opens the table context menu when the touch is on a
  // table header / column header / chart, and the grid context menu when it's
  // on a regular cell while in cellSelected/multiSelect mode. View mode has no
  // selection, so long-press there is a no-op.
  private startMobileLongPress(world: Point) {
    this.clearMobileLongPress();
    this.mobileLongPressWorld = world.clone();
    this.mobileLongPressTimeout = window.setTimeout(() => {
      this.mobileLongPressTimeout = undefined;
      this.fireMobileLongPress();
    }, MOBILE_LONG_PRESS_DURATION);
  }

  private clearMobileLongPress() {
    if (this.mobileLongPressTimeout) {
      window.clearTimeout(this.mobileLongPressTimeout);
      this.mobileLongPressTimeout = undefined;
    }
    this.mobileLongPressWorld = undefined;
  }

  private fireMobileLongPress() {
    const world = this.mobileLongPressWorld;
    this.mobileLongPressWorld = undefined;
    if (!world) return;

    // Tables take priority - hit-test with isRightClick=true so we do not
    // toggle sort or mutate other state during the test.
    const tableDown = content.cellsSheet.tables.pointerDown(world, true);

    // Outside a table the grid context menu only opens once the user has
    // already entered cell-select mode. View mode has no selection, so a
    // long-press there falls through to the normal tap handling on release.
    const inCellSelectMode = this.mobileCellSelectedTap !== undefined;
    if (!tableDown && !inCellSelectMode) return;

    // If we paused viewport drag for a potential drag-to-expand on a selected
    // cell, resume it before swapping into long-press mode.
    const wasOnSelectedCell = this.mobileCellSelectedTap?.onSelectedCell;

    // Cancel all pending tap/drag state so the eventual pointerUp does not
    // also try to handle this gesture as a normal tap.
    this.mobileViewTap = undefined;
    this.mobileCellSelectedTap = undefined;
    this.mobileScreenPosition = undefined;
    this.active = false;
    this.previousPosition = undefined;
    this.position = undefined;
    this.pointerMoved = false;
    this.clearMobileDoubleTap();

    if (wasOnSelectedCell) {
      pixiApp.viewport.resumeDrag();
      pixiApp.viewport.disableMouseEdges();
    }

    if (tableDown) {
      this.openTableContextMenu(world, tableDown);
    } else {
      this.openGridContextMenu(world);
    }
  }

  // Selects the table or column under the long-press point and emits the
  // matching context-menu event - mirrors the right-click flow in
  // PointerTable.pointerDown so desktop and mobile produce the same menu.
  private openTableContextMenu(world: Point, tableDown: TablePointerDownResult) {
    const cursor = sheets.sheet.cursor;
    if (tableDown.type === 'column-name' && tableDown.column !== undefined) {
      const columnName = tableDown.table.columns.find((c) => c.valueIndex === tableDown.column)?.name;
      if (!columnName) return;
      if (!cursor.isTableColumnSelected(tableDown.table.name, tableDown.column)) {
        cursor.selectTable(tableDown.table.name, columnName, false, false);
      }
    } else {
      cursor.selectTable(tableDown.table.name, undefined, false, false);
    }

    events.emit('contextMenu', {
      type: tableDown.type === 'column-name' ? ContextMenuType.TableColumn : ContextMenuType.Table,
      world,
      column: tableDown.table.x,
      row: tableDown.table.y,
      table: tableDown.table,
      selectedColumn: tableDown.column,
    });
    events.emit('setDirty', { cursor: true });
  }

  // Opens the regular grid context menu at the long-press point - mirrors the
  // right-click flow in pointerUp(). If the long-pressed cell isn't already in
  // the current selection, the cursor moves to it first so menu actions
  // operate on the cell the user pressed.
  private openGridContextMenu(world: Point) {
    const sheet = sheets.sheet;
    const cursor = sheet.cursor;
    const { column, row } = sheet.getColumnRowFromScreen(world.x, world.y);

    if (!cursor.contains(column, row)) {
      cursor.moveTo(column, row, { ensureVisible: false });
    }

    const codeCell = content.cellsSheet.tables.getCodeCellIntersects({ x: column, y: row });
    if (codeCell && codeCell.language !== 'Import') {
      events.emit('contextMenu', {
        type: ContextMenuType.Grid,
        world,
        column: codeCell.x,
        row: codeCell.y,
        table: codeCell,
      });
    } else {
      events.emit('contextMenu', {
        type: ContextMenuType.Grid,
        world,
        column,
        row,
        // _could_ have an associated table if it's a cell inside a table on the grid
        table: content.cellsSheet.tables.getCodeCellIntersects(cursor.position),
      });
    }
    events.emit('setDirty', { cursor: true });
  }

  destroy() {
    this.clearDoubleClick();
    this.clearMobileDoubleTap();
    this.clearMobileLongPress();
  }

  pointerDownColumnName(world: Point, column: number, row: number) {
    this.previousPosition = new Point(column, row);
    this.positionRaw = world;
    this.pointerMoved = false;
    this.position = new Point(column, row);
    this.active = true;
  }
}
